import { UTC, isNullOrUndefined, fromEnumerable, guid } from 'rewire-common';
import { observable, replace } from 'rewire-core';
import {
  ITemplate,
  IGroup,
  ITimecard,
  IJob,
  IOccupation,
  IReason,
  ISimplifiedBatch,
  BatchStatusType,
  getMultipleObject,
  getMultiple,
  IEmployeeTimecardStatus,
  IValidTransition,
} from '../DataTypes';
import { IRolledUpTimecard } from './DailyRollupModel';
import timecardModel, { TimecardModelBase } from './TimecardModel';

export interface IRollupReason {
  name: string;
  units: number;
  factor: number;
  amount: number;
  columnOrder: number;
}

export default class RolledUpTimecard implements IRolledUpTimecard {
  earnedDate!: UTC;
  group: any;
  template: any;
  job: any;
  start: any;
  end: any;
  units: any;
  rate: any;
  gross: any;
  reason: any;
  occupation: any;
  accountCode: any;
  otherPay: any;
  reasons!: IRollupReason[];
  timecards!: ITimecard[];
  timecardStatus!: IEmployeeTimecardStatus | null;
  id: string = guid();
  setTimeoutRunningTimecards: boolean = false;
  setTimeoutRunningOtherPayments: boolean = false;

  static create(earnedDate: UTC, timecards: ITimecard[], status: IEmployeeTimecardStatus | null): IRolledUpTimecard {
    const rolledUpTimecard = observable(new RolledUpTimecard());
    rolledUpTimecard.initialize(earnedDate, timecards, status);
    return rolledUpTimecard;
  }

  get manualTimecard(): boolean {
    return this.timecardStatus?.manual || false;
  }

  set manualTimecard(value: boolean) {
    if (this.timecardStatus) {
      this.timecardStatus.manual = value;
    }
  }

  get timecardModel(): TimecardModelBase {
    return timecardModel;
  }

  setBatch(newBatch: ISimplifiedBatch | undefined): void {
    const timecards = this.timecards.filter((t) => this.timecardModel.isCompleteTimecard(t));
    timecards.forEach((t) => {
      if (!newBatch) {
        delete t.batch;
      } else {
        t.batch = Object.assign({}, t.batch, newBatch);
      }
    });

    const otherPayments = this.timecardModel.otherPayments.filter(
      (t) => this.timecardModel.isCompleteOtherPayment(t) && t.earnedDate?.equals(this.earnedDate),
    );
    otherPayments.forEach((t) => {
      if (!newBatch) {
        delete t.batch;
      } else {
        t.batch = Object.assign({}, t.batch, newBatch);
      }
    });
  }

  setStatus(newStatus: IEmployeeTimecardStatus): void {
    replace(this.timecardStatus, newStatus);
  }

  get status(): string | undefined {
    return this.timecardStatus?.status;
  }

  get isReversal(): boolean {
    return this.timecards.some((t) => t.timecardType === 'r');
  }

  get isCorrection(): boolean {
    const result = this.timecards.some(
      (t) => t.timecardType === 'c' && this.timecardModel.reversals.some((r) => r.earnedDate.equals(this.earnedDate)),
    );
    return result;
  }

  get batchStatus(): BatchStatusType | undefined {
    return this.timecardStatus?.batch?.status || 'not_in_batch';
  }

  get batchName(): string | undefined {
    return this.timecardStatus?.batch?.name;
  }

  get allowableTransitions(): IValidTransition[] {
    return this.timecardStatus?.allowableTransitions || [];
  }

  get canEditTimecard(): boolean {
    return this.isReversal ? false : this.batchStatus !== 'closed' && !this.timecardStatus?.locked;
  }

  get canTransitionTimecard(): boolean {
    return this.isReversal ? false : this.batchStatus !== 'closed';
  }

  getStart() {
    return fromEnumerable<UTC>(
      this.timecards
        .filter((t) => this.isRelevantCategoryForStartEnd(t) && this.timecardModel.isCompleteTimecard(t))
        .map((t) => t.start!),
    ).min();
  }
  getEnd() {
    return fromEnumerable<UTC>(
      this.timecards
        .filter((t) => this.isRelevantCategoryForStartEnd(t) && this.timecardModel.isCompleteTimecard(t))
        .map((t) => t.end!),
    ).max();
  }

  getUnits() {
    return this.timecards.reduce((totalUnits, thisTimecard) => {
      if (!this.timecardModel.isTimecardDateValid(thisTimecard, this.earnedDate)) return totalUnits;
      if (isNullOrUndefined(thisTimecard.units)) return totalUnits;
      if (!this.isRelevantCategoryForUnits(thisTimecard)) return totalUnits;

      // at this point, totalUnits is a number
      // return the sum of this previous total and the current timecard's units
      return totalUnits! + thisTimecard.units;
    }, 0);
  }

  getRate() {
    return this.timecards.reduce((rate, thisTimecard) => {
      if (!this.timecardModel.isTimecardDateValid(thisTimecard, this.earnedDate)) return rate;
      if (!this.shouldEvaluateRate(thisTimecard)) return rate;

      if (isNullOrUndefined(rate)) return thisTimecard.rate;
      if (rate !== thisTimecard.rate) return getMultiple();
      return rate;
    }, undefined);
  }

  canCalculateAmount(timecard: ITimecard): boolean {
    return timecard.units !== undefined && timecard.rate !== undefined && timecard.factor !== undefined;
  }

  getGross() {
    return this.timecards.reduce((totalAmount, thisTimecard) => {
      if (!this.timecardModel.isTimecardDateValid(thisTimecard, this.earnedDate)) return totalAmount;
      if (!this.canCalculateAmount(thisTimecard)) return totalAmount;

      const currentAmount = thisTimecard.units! * thisTimecard.rate! * thisTimecard.factor!;
      return totalAmount + currentAmount;
    }, 0);
  }

  getReason() {
    return this.timecards.reduce((reason: IReason, thisTimecard) => {
      if (!this.timecardModel.isTimecardDateValid(thisTimecard, this.earnedDate)) return reason;
      if (!this.timecardModel.isCompleteTimecard(thisTimecard)) return reason;
      if (!this.shouldEvaluateReason(thisTimecard)) return reason;

      if (!reason?.id) return thisTimecard.reason;
      if (!thisTimecard.reason?.id) return reason;
      if (reason.id! !== thisTimecard.reason.id) return getMultipleObject();
      return reason;
    }, undefined);
  }

  getOccupation() {
    return this.timecards.reduce((occupation: IOccupation, thisTimecard) => {
      if (!this.timecardModel.isTimecardDateValid(thisTimecard, this.earnedDate)) return occupation;
      if (!this.timecardModel.isCompleteTimecard(thisTimecard)) return occupation;

      if (!occupation?.id) return thisTimecard.occupation;
      if (!thisTimecard.occupation?.id) return occupation;
      if (occupation.id !== thisTimecard.occupation.id) return getMultipleObject();
      return occupation;
    }, undefined);
  }

  getAccountCode() {
    return this.timecards.reduce((accountCode, thisTimecard) => {
      if (!this.timecardModel.isTimecardDateValid(thisTimecard, this.earnedDate)) return accountCode;

      if (isNullOrUndefined(accountCode)) return thisTimecard.accountCode;
      if (isNullOrUndefined(thisTimecard.accountCode)) return accountCode;
      if (accountCode !== thisTimecard.accountCode) return getMultiple();
      return accountCode;
    }, undefined);
  }

  getOtherPay() {
    return this.timecardModel.otherPayments.reduce((totalAmount, thisPayment) => {
      if (!thisPayment.earnedDate?.isValid) return totalAmount;
      if (!thisPayment.earnedDate.equals(this.earnedDate)) return totalAmount;
      if (isNullOrUndefined(thisPayment.units) || isNullOrUndefined(thisPayment.rate) || isNullOrUndefined(thisPayment.factor))
        return totalAmount;

      const currentAmount = thisPayment.units! * thisPayment.rate! * thisPayment.factor!;
      return totalAmount + currentAmount;
    }, 0);
  }

  getTemplate() {
    return this.timecards.reduce((template: ITemplate | undefined, thisTimecard) => {
      const t = template && { ...template };
      const thisTimecardT = thisTimecard.template && { ...thisTimecard.template };
      if (!this.timecardModel.isTimecardDateValid(thisTimecard, this.earnedDate)) return t;

      if (!template?.id) return thisTimecardT;
      if (!thisTimecard.template?.id) return t;
      if (template.id !== thisTimecard.template.id) return getMultipleObject();
      return t;
    }, undefined);
  }

  getGroup() {
    return this.timecards.reduce((group: IGroup | undefined, thisTimecard) => {
      const t = group && { ...group };
      const thisTimecardT = thisTimecard.group && { ...thisTimecard.group };
      if (!this.timecardModel.isTimecardDateValid(thisTimecard, this.earnedDate)) return t;

      if (!group?.id) return thisTimecardT;
      if (!thisTimecard.group?.id) return t;
      if (group.id !== thisTimecard.group.id) return getMultipleObject();
      return t;
    }, undefined);
  }

  getJob() {
    return this.timecards.reduce((job: IJob, thisTimecard) => {
      if (!this.timecardModel.isTimecardDateValid(thisTimecard, this.earnedDate)) return job;

      if (!job?.id) return thisTimecard.job;
      if (!thisTimecard.job?.id) return job;
      if (job.id !== thisTimecard.job.id) return getMultipleObject();
      return job;
    }, undefined);
  }

  protected initialize(earnedDate: UTC, timecards: ITimecard[], status: IEmployeeTimecardStatus | null) {
    this.earnedDate = earnedDate;
    this.reasons = [];
    this.timecards = timecards;
    this.timecardStatus = status;
    this.processReasons();

    this.group = this.getGroup();
    this.template = this.getTemplate();
    this.job = this.getJob();
    this.start = this.getStart();
    this.end = this.getEnd();
    this.units = this.getUnits();
    this.rate = this.getRate();
    this.gross = this.getGross();
    this.reason = this.getReason();
    this.occupation = this.getOccupation();
    this.accountCode = this.getAccountCode();
    this.otherPay = this.getOtherPay();

    this.timecardModel.sortTimecards(this.timecards);
  }

  update(timecards: ITimecard[], status: IEmployeeTimecardStatus | null) {
    this.timecards = timecards;
    this.timecardStatus = status;

    this.processReasons();

    this.group = this.getGroup();
    this.template = this.getTemplate();
    this.job = this.getJob();
    this.start = this.getStart();
    this.end = this.getEnd();
    this.units = this.getUnits();
    this.rate = this.getRate();
    this.gross = this.getGross();
    this.reason = this.getReason();
    this.occupation = this.getOccupation();
    this.accountCode = this.getAccountCode();
    this.otherPay = this.getOtherPay();
  }

  get lastEdited(): string | undefined {
    if (!this.timecardStatus) return undefined;
    const who = this.timecardStatus.lastEditedBy || '«unknown»';
    const when = this.timecardStatus.lastEditedDate
      ? `on ${this.timecardStatus.lastEditedDate.toDateString()} at ${this.timecardStatus.lastEditedDate.toTimeString()}`
      : undefined;
    return `Last edited by ${who} ${when}`;
  }

  protected shouldEvaluateRate(timecard: ITimecard): boolean {
    if (!this.timecardModel.isCompleteTimecard(timecard)) return false;
    if (!timecard.reason || !timecard.reason.category) return false;
    if (['premium', 'break'].includes(timecard.reason.category)) return false;

    // worked, absence
    return true;
  }

  protected shouldEvaluateReason(timecard: ITimecard): boolean {
    if (!this.timecardModel.isCompleteTimecard(timecard)) return false;
    if (!timecard.reason || !timecard.reason.category) return false;
    if (['premium', 'break'].includes(timecard.reason.category)) return false;

    return true;
  }

  protected shouldProcessReason(timecard: ITimecard): boolean {
    return !!timecard.reason && typeof timecard.factor === 'number';
    // return (!!timecard.reason && !!timecard.factor && (timecard.factor > 0));
  }

  protected processReasons() {
    this.reasons.length = 0;
    for (const timecard of this.timecards) {
      if (!this.shouldProcessReason(timecard)) {
        continue;
      }

      const key: string = this.makeReasonKey(timecard);
      const columnOrder: number = this.timecardModel.determineSortOrder(timecard);
      let reason = this.reasons.find((r) => r.name === key); // && r.columnOrder === columnOrder);
      if (!reason) {
        reason = {
          name: key,
          columnOrder,
          factor: timecard.factor!,
          units: 0,
          amount: 0,
        };
        this.reasons.push(reason);
      }
      reason.units += timecard.units || 0;
      reason.amount += timecard.units! * timecard.rate! * timecard.factor!;
    }
  }

  protected makeReasonKey(timecard: ITimecard): string {
    return `${timecard.reason!.name} ${timecard.factor!.toFixed(1)}x`;
  }

  protected isRelevantCategoryForUnits(timecard: ITimecard): boolean {
    return (timecard.reason?.category && ['worked', 'overtime', 'absence'].includes(timecard.reason!.category!)) || false;
  }

  protected isRelevantCategoryForStartEnd(timecard: ITimecard): boolean {
    return (timecard.reason?.category && ['worked', 'overtime', 'break', 'absence'].includes(timecard.reason!.category!)) || false;
  }
}
